import SectionCard from './SectionCard';
import ToggleField from './ToggleField';
import Segmented from '@/components/base/Segmented';
import {
  type SalesSettings,
  type SaleStatus,
  formatInvoiceNumber,
} from '@/lib/salesSettings';

interface SectionProps {
  settings: SalesSettings;
  patch: (partial: Partial<SalesSettings>) => void;
}

export function NumeracionSection({ settings, patch }: SectionProps) {
  return (
    <SectionCard
      icon="ri-file-list-3-line"
      title="Numeración de facturas"
      description="Configura el formato del número de factura. La numeración será automática y sin duplicados."
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-foreground-800 mb-1.5">Prefijo</label>
          <input
            type="text"
            value={settings.invoice_prefix}
            onChange={(e) => patch({ invoice_prefix: e.target.value })}
            placeholder="FAC"
            className="w-full rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400 transition"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-foreground-800 mb-1.5">
            Número inicial
          </label>
          <input
            type="number"
            min={0}
            value={settings.invoice_start_number}
            onChange={(e) =>
              patch({ invoice_start_number: e.target.value === '' ? 0 : Number(e.target.value) })
            }
            className="w-full rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400 transition"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-foreground-800 mb-1.5">
            Cantidad de dígitos
          </label>
          <input
            type="number"
            min={1}
            max={12}
            value={settings.invoice_digits}
            onChange={(e) =>
              patch({ invoice_digits: e.target.value === '' ? 6 : Number(e.target.value) })
            }
            className="w-full rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400 transition"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-foreground-800 mb-1.5">
            Último número utilizado
          </label>
          <div className="flex items-center gap-3 rounded-md border border-background-300 bg-background-100 px-4 py-2.5">
            <span className="text-sm font-medium text-foreground-900">
              {settings.invoice_last_number}
            </span>
            <span className="ml-auto inline-flex items-center gap-1 text-xs text-foreground-500">
              <i className="ri-lock-line" /> Automático
            </span>
          </div>
        </div>
      </div>

      <div className="rounded-md bg-accent-50 border border-accent-200 px-4 py-3">
        <p className="text-xs font-medium text-foreground-500 mb-1.5">Resultado esperado</p>
        <p className="font-mono text-base font-semibold text-foreground-950">
          {formatInvoiceNumber(settings)}
        </p>
      </div>
    </SectionCard>
  );
}

export function EstadoVentaSection({ settings, patch }: SectionProps) {
  return (
    <SectionCard
      icon="ri-flag-2-line"
      title="Estado inicial de una venta"
      description="Estado con el que se registrará una venta al finalizarla."
    >
      <Segmented
        value={settings.sale_initial_status}
        onChange={(v) => patch({ sale_initial_status: v as SaleStatus })}
        options={[
          { value: 'completed', label: 'Completada' },
          { value: 'pending', label: 'Pendiente' },
          { value: 'cancelled', label: 'Anulada' },
        ]}
      />
      {settings.sale_initial_status === 'pending' && (
        <p className="text-xs text-foreground-500">
          Las funciones de cuentas por cobrar se implementarán en una etapa posterior.
        </p>
      )}
    </SectionCard>
  );
}

export function RedondeoSection({ settings, patch }: SectionProps) {
  return (
    <SectionCard
      icon="ri-rounded-corner"
      title="Redondeo"
      description="Define si los totales se redondean automáticamente."
    >
      <ToggleField
        label="¿Utilizar redondeo en los totales?"
        checked={settings.rounding_enabled}
        onChange={(v) => patch({ rounding_enabled: v })}
      />
    </SectionCard>
  );
}

export function InventarioSection({ settings, patch }: SectionProps) {
  return (
    <SectionCard
      icon="ri-stack-line"
      title="Control de inventario en ventas"
      description="Define cómo se comportará el punto de venta con el inventario."
    >
      <ToggleField
        label="¿Permitir vender productos sin existencia?"
        description="Si está desactivado, el punto de venta impedirá vender más de lo disponible."
        checked={settings.allow_sell_without_stock}
        onChange={(v) => patch({ allow_sell_without_stock: v })}
      />
      <div className="border-t border-background-100 pt-5">
        <ToggleField
          label="¿Permitir vender productos desactivados?"
          description="Los productos desactivados no aparecerán en el punto de venta."
          checked={settings.allow_sell_inactive}
          onChange={(v) => patch({ allow_sell_inactive: v })}
        />
      </div>
    </SectionCard>
  );
}

export function ConfirmacionSection({ settings, patch }: SectionProps) {
  return (
    <SectionCard
      icon="ri-question-answer-line"
      title="Confirmación"
      description="Solicita confirmación antes de finalizar una venta."
    >
      <ToggleField
        label="¿Solicitar confirmación antes de finalizar una venta?"
        checked={settings.require_sale_confirmation}
        onChange={(v) => patch({ require_sale_confirmation: v })}
      />
      {settings.require_sale_confirmation && (
        <div className="rounded-md bg-background-100 border border-background-200 px-4 py-3 text-sm text-foreground-700">
          El punto de venta mostrará: <span className="font-medium">“¿Desea finalizar esta venta?”</span>
        </div>
      )}
    </SectionCard>
  );
}